import {
  faAngleDown,
  faAngleUp,
  faArrowDownLeftAndArrowUpRightToCenter,
  faArrowUpRightAndArrowDownLeftFromCenter,
  faTimes,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import styled from 'styled-components';
import { BLACK, BLACK2, BORDER_GRAY, NEUTRAL70, WHITE } from '../../theme';
import { shortYearDate } from '../../utils/date';
import Avatar from '../reusable/Avatar';
import { ButtonSize } from '../reusable/Button';
import { IconButton } from '../reusable/Button/IconButton';
import Text, { TextColor, TextKind } from '../reusable/Text';
import { NoteSize } from './Context';
import type { NoteType } from './Note';

const NoteHeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 8px;
  height: 40px;
  border-bottom: 1px solid ${BORDER_GRAY};
`;

const StyledNoteHeaderActionContent = styled.div`
  display: flex;
  align-items: center;
`;

const StyledTitleInput = styled.input`
  box-sizing: border-box;
  width: 100%;
  margin-right: 16;
  border: 1px solid transparent;
  border-radius: 8px;
  font-family: reader;
  font-size: 14px;
  background-color: ${WHITE};
  &:hover {
    border: 1px solid ${BORDER_GRAY};
  }
  padding: 8px;
  
  &::placeholder {
    color: ${NEUTRAL70};
    opacity: 1;
  }
  color: ${BLACK2};
  font-weight: 600;
  font-size 14px;
  &:focus {
    border-color: ${BLACK};
    font-weight: normal;
  }
`;

const AvatarContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
`;

interface EditableTitleProps {
  title: string;
  onTitleChange: (title: string) => void;
  onTitleFocus?: () => void;
}

const EditableTitle: React.FC<EditableTitleProps> = ({
  title,
  onTitleChange,
  onTitleFocus,
}) => (
  <StyledTitleInput
    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
      onTitleChange(e.target.value);
    }}
    data-testid="note-header-title"
    value={title}
    placeholder="Insert a title for the note"
    onFocus={onTitleFocus}
  />
);

interface UneditableTitleProps {
  note: NoteType;
  loading: boolean;
}

const UneditableTitle: React.FC<UneditableTitleProps> = ({ note, loading }) => {
  const date = note.updatedAt ? shortYearDate(note.updatedAt) : null;

  if (loading) {
    return <div />;
  }

  const coach = note.coach || note.lastUpdatedBy;

  return (
    <div>
      <Text kind={TextKind.LabelL} testId="note-header-title">
        {note.title}
      </Text>
      <AvatarContainer>
        {coach && (
          <>
            <Avatar size={20} user={coach} />{' '}
            <Text kind={TextKind.SubtitleXs} color={TextColor.Secondary}>
              {coach?.firstName} {coach?.lastName} • {date}
            </Text>
          </>
        )}
        {!coach && (
          <Text kind={TextKind.SubtitleXs} color={TextColor.Secondary}>
            {date}
          </Text>
        )}
      </AvatarContainer>
    </div>
  );
};

interface HeaderProps {
  noteSize: NoteSize;
  handleMaximizeNote: () => void;
  handleOpenNote: () => void;
  handleCloseNote: () => void;
  onTitleChange: (title: string) => void;
  note: NoteType;
  coachId?: string;
  onTitleFocus?: () => void;
  loading: boolean;
}

export const NoteHeader: React.FC<HeaderProps> = ({
  noteSize,
  handleMaximizeNote,
  handleOpenNote,
  handleCloseNote,
  onTitleChange,
  onTitleFocus,
  note,
  coachId = null,
  loading,
}) => {
  const isEditable = (!note.id || note.coach?.id === coachId) && !loading;

  return (
    <NoteHeaderContainer>
      {isEditable && (
        <EditableTitle
          title={note.title ?? ''}
          onTitleChange={onTitleChange}
          onTitleFocus={onTitleFocus}
        />
      )}
      {!isEditable && <UneditableTitle note={note} loading={loading} />}
      <div>
        <StyledNoteHeaderActionContent>
          {noteSize !== NoteSize.Minimized && (
            <IconButton
              testId="note-header-maximizing-button"
              size={ButtonSize.Small}
              icon={
                <FontAwesomeIcon
                  data-testid="note-header-maximizing-button-icon"
                  icon={
                    noteSize === NoteSize.Maximized
                      ? faArrowDownLeftAndArrowUpRightToCenter
                      : faArrowUpRightAndArrowDownLeftFromCenter
                  }
                  fontSize={16}
                />
              }
              onClick={handleMaximizeNote}
            />
          )}
          <IconButton
            testId="note-header-expansion-button"
            size={ButtonSize.Small}
            icon={
              <FontAwesomeIcon
                data-testid="note-header-expansion-button-icon"
                icon={noteSize === NoteSize.Minimized ? faAngleUp : faAngleDown}
                fontSize={20}
              />
            }
            onClick={handleOpenNote}
          />
          <IconButton
            testId="note-header-close-button"
            size={ButtonSize.Small}
            icon={
              <FontAwesomeIcon
                icon={faTimes}
                fontSize={20}
                data-testid="note-header-close-button-icon"
              />
            }
            onClick={handleCloseNote}
            ariaLabel="close note"
          />
        </StyledNoteHeaderActionContent>
      </div>
    </NoteHeaderContainer>
  );
};
